import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import {
	AlertCircle,
	CreditCard01,
	File06,
	Mail01,
	MarkerPin01,
	Phone,
} from "@untitledui/icons";
import { useState } from "react";
import { Checkbox } from "@/components/Atoms/Checkbox/Checkbox";
import { Input } from "@/components/Atoms/Input/preferencesInput";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { ContactCard } from "@/components/base/contact-card";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field";
import type { SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { EmptyValue, PhoneNumber } from "@/components/common";
import { TableHeader } from "@/components/Organisms/TableHeader/TableHeader";
import { Illustration } from "@/components/shared-assets/illustrations";
import {
	useEditBillingContactForm,
	useEditPrimaryContactForm,
} from "@/forms/(org-admin)/contact/edit-contact.form.hooks.tsx";
import { ContactTypeCodes } from "@/forms/(org-admin)/contact/edit-contact.form.types";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	applyAuthenticatedTranslations,
	markFieldInfoLocalizationsAppliedForOrganization,
	parseLocalizationsResponse,
} from "@/i18n-authenticated";
import { fieldInfoKeys, fieldInfoQueryOptions } from "@/services/field-info";
import { organizationQueryOptions } from "@/services/organization";
import { platformSettingService } from "@/services/platform-settings";
import { isValidUSState } from "@/utils/us-states";
import { isValidUSZipcode } from "@/utils/us-zipcode";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/settings/preferences",
)({
	component: RouteComponent,
});

const SUB_ORGANIZATION_TERM_KEY = "SubOrganization.entityLabel";

function RouteComponent() {
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/settings/preferences",
	});

	// Edit mode states
	const [isPrimaryEditing, setIsPrimaryEditing] = useState(false);
	const [isBillingEditing, setIsBillingEditing] = useState(false);
	const [isTerminologyEditing, setIsTerminologyEditing] = useState(false);
	const [isTerminologySaving, setIsTerminologySaving] = useState(false);
	// Form hooks for managing contact data
	const primaryContactForm = useEditPrimaryContactForm(params.orgId, {
		onSuccess: () => {
			setIsPrimaryEditing(false);
			primaryContactForm.resetToCurrentData();
		},
	});

	const billingContactForm = useEditBillingContactForm(params.orgId, {
		onSuccess: () => {
			setIsBillingEditing(false);
			billingContactForm.resetToCurrentData();
		},
	});

	// Fetch organization contacts for loading states
	const orgContacts = useQuery({
		...organizationQueryOptions.contacts.list(params.orgId),
		retry: 1,
	});
	const primaryContact = orgContacts.data?.find(
		(contact) =>
			contact.contactType === "Primary" ||
			contact.contactTypeCode === ContactTypeCodes.PRIMARY,
	);
	const billingContact = orgContacts.data?.find(
		(contact) =>
			contact.contactType === "Billing" ||
			contact.contactTypeCode === ContactTypeCodes.BILLING,
	);
	const canEditPrimaryContact = useEntityActionAllowed({
		name: "UpdateOrganizationContact",
		entityType: "OrganizationContact",
		entityId: primaryContact?.id,
		enabled: Boolean(primaryContact?.id),
	});
	const canEditBillingContact = useEntityActionAllowed({
		name: "UpdateOrganizationContact",
		entityType: "OrganizationContact",
		entityId: billingContact?.id,
		enabled: Boolean(billingContact?.id),
	});
	const canEditTerminology = useEntityActionAllowed({
		name: "SetConfigurationValue",
		enabled: params.orgId.length > 0,
	});

	// Local state for sub-organization label (single field, no form)
	const [subOrganizationLabel, setSubOrganizationLabel] = useState("");

	// Check loading and error states
	const isLoading = orgContacts.isLoading;
	const hasError = orgContacts.isError;

	// User items for select dropdowns (each form has its own infinite list)
	const userSelectItems: SelectItemType[] =
		primaryContactForm.users?.map((user) => ({
			id: user.userId,
			label: `${user.firstName} ${user.lastName}`,
			supportingText: user.email,
		})) ?? [];

	const billingUserSelectItems: SelectItemType[] =
		billingContactForm.users?.map((user) => ({
			id: user.userId,
			label: `${user.firstName} ${user.lastName}`,
			supportingText: user.email,
		})) ?? [];

	// Format address for display
	const formatAddress = (
		address: {
			street1?: string | null;
			city?: string | null;
			state?: string | null;
			postalCode?: string | null;
		} | null,
	) => {
		if (!address) return null;
		const parts = [
			address.street1,
			address.city,
			address.state,
			address.postalCode,
		].filter(Boolean);
		return parts.length > 0 ? parts.join(", ") : null;
	};

	// Handle edit button clicks
	const handlePrimaryEdit = () => {
		if (!canEditPrimaryContact.allowed) return;
		primaryContactForm.resetToCurrentData();
		setIsPrimaryEditing(true);
	};

	const handleBillingEdit = () => {
		if (!canEditBillingContact.allowed) return;
		billingContactForm.resetToCurrentData();
		setIsBillingEditing(true);
	};

	// Handle cancel
	const handlePrimaryCancel = () => {
		primaryContactForm.resetToCurrentData();
		setIsPrimaryEditing(false);
	};

	const handleBillingCancel = () => {
		billingContactForm.resetToCurrentData();
		setIsBillingEditing(false);
	};

	// Terminology card: edit/cancel/save (local state only; no form)
	const handleTerminologyEdit = () => {
		if (!canEditTerminology.allowed) return;
		setSubOrganizationLabel(t("subOrganization.entityLabel"));
		setIsTerminologyEditing(true);
	};
	const handleTerminologyCancel = () => {
		setSubOrganizationLabel(t("subOrganization.entityLabel"));
		setIsTerminologyEditing(false);
	};
	const handleTerminologySave = async () => {
		if (!canEditTerminology.allowed) return;
		setIsTerminologySaving(true);
		try {
			const orgId = params.orgId;
			const current =
				await platformSettingService.getOrganizationLocalizations(orgId);
			const body: Record<string, Record<string, string>> = {};
			if (current.length > 0) {
				for (const loc of current) {
					body[""] = {
						...(loc.values ?? {}),
						[SUB_ORGANIZATION_TERM_KEY]: subOrganizationLabel,
					};
				}
			} else {
				body[""] = { [SUB_ORGANIZATION_TERM_KEY]: subOrganizationLabel };
			}
			await platformSettingService.putOrganizationLocalizations(orgId, body);
			await queryClient.invalidateQueries({
				queryKey: fieldInfoKeys.label("SubOrganization", {
					organizationId: orgId,
				}),
			});
			await queryClient.invalidateQueries({
				queryKey: fieldInfoKeys.localizations({ organizationId: orgId }),
			});
			// Drop unscoped localizations cache so a later beforeLoad cannot re-apply stale session defaults over org copy.
			await queryClient.removeQueries({
				queryKey: fieldInfoKeys.localizations(undefined),
			});
			const raw = await queryClient.fetchQuery(
				fieldInfoQueryOptions.localizations({ organizationId: orgId }),
			);
			applyAuthenticatedTranslations(parseLocalizationsResponse(raw));
			markFieldInfoLocalizationsAppliedForOrganization(orgId);

			setIsTerminologyEditing(false);
		} finally {
			setIsTerminologySaving(false);
		}
	};

	// Handle save
	const handlePrimarySave = async () => {
		if (!canEditPrimaryContact.allowed) return;
		await primaryContactForm.handleSubmit();
	};

	const handleBillingSave = async () => {
		if (!canEditBillingContact.allowed) return;
		await billingContactForm.handleSubmit();
	};

	// Error state
	if (hasError && !isLoading) {
		return (
			<div className="space-y-0">
				<TableHeader
					title={t("org-admin.settings.preferences.table.title")}
					description={t("org-admin.settings.preferences.table.description")}
				/>
				<div className="flex flex-col items-center justify-center h-64 gap-4 mx-6">
					<Illustration type="cloud" size="sm">
						<div className="bg-error-500 rounded-full p-3">
							<AlertCircle className="size-6 text-white" />
						</div>
					</Illustration>
					<div className="flex flex-col items-center justify-center pt-4">
						<p className="text-gray-900 font-semibold text-md">
							{t("org-admin.users.errors.errorLoadingPage")}
						</p>
						<p className="text-gray-600 font-normal text-sm mt-2">
							{t("org-admin.users.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-0">
			{/* Page Header */}
			<TableHeader
				title={t("org-admin.settings.preferences.table.title")}
				description={t("org-admin.settings.preferences.table.description")}
			/>

			{/* Main Content */}
			<div className="mx-6 space-y-8">
				{/* Contacts Section */}
				{isLoading ? (
					<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
						<LoadingIndicator type="line-simple" size="xl" supportingText />
					</div>
				) : (
					<section className="space-y-6">
						<h2 className="text-md font-semibold text-gray-900 mb-4">
							{t("org-admin.settings.preferences.contacts.sectionTitle")}
						</h2>

						{/* Contact Cards Grid - items-start prevents cards from stretching when one enters edit mode */}
						<div className="flex flex-row justify-between gap-6 items-start">
							{/* Primary Contact Card */}
							<ContactCard
								title={t(
									"org-admin.settings.preferences.contacts.primaryContact",
								)}
								saveTestId="org-preferences-primary-contact-save-btn"
								editTestId="org-preferences-primary-contact-edit-btn"
								isEditing={
									isPrimaryEditing && canEditPrimaryContact.allowed
								}
								onEdit={
									canEditPrimaryContact.allowed
										? handlePrimaryEdit
										: undefined
								}
								onCancel={handlePrimaryCancel}
								onSave={handlePrimarySave}
								isSaving={primaryContactForm.isUpdating}
								isSaveDisabled={!primaryContactForm.isDirty}
								className="w-1/2"
							>
								<primaryContactForm.Subscribe
									selector={(state) => state.values}
								>
									{(_values) => {
										// Check if a user has been selected - all fields disabled until Name is selected
										const isUserSelected = !!_values.userId;

										return (
											<DetailFieldGrid>
												<DetailFieldSection>
													<DetailField
														label={t(
															"org-admin.settings.preferences.contacts.fields.name",
														)}
														value={
															[
																primaryContactForm.primaryContact?.firstName,
																primaryContactForm.primaryContact?.lastName,
															]
																.filter(Boolean)
																.join(" ") || null
														}
														isEditMode={isPrimaryEditing}
														editComponent={
															<primaryContactForm.AppField name="userId">
																{(field) => (
																	<field.SelectField
																		field={field}
																		data-testid="org-preferences-primary-contact-user-select"
																		label={t(
																			"org-admin.settings.preferences.contacts.fields.name",
																		)}
																		items={userSelectItems}
																		placeholder={t(
																			"org-admin.settings.preferences.contacts.placeholders.selectUser",
																		)}
																		showSupportingTextInValue={false}
																		onLoadMore={
																			primaryContactForm.fetchNextUsersPage
																		}
																		hasNextPage={
																			primaryContactForm.hasNextUsersPage
																		}
																		isFetchingNextPage={
																			primaryContactForm.isFetchingNextUsersPage
																		}
																		onItemSelect={(key) => {
																			// Auto-fill other fields when a user is selected
																			if (key) {
																				primaryContactForm.handleUserSelect(
																					key,
																				);
																			}
																		}}
																	>
																		{(item: SelectItemType) => (
																			<SelectItem
																				id={item.id}
																				textValue={item.label}
																			>
																				<div className="flex flex-col">
																					<span>{item.label}</span>
																					<span className="text-xs text-gray-500">
																						{item.supportingText}
																					</span>
																				</div>
																			</SelectItem>
																		)}
																	</field.SelectField>
																)}
															</primaryContactForm.AppField>
														}
													/>

													<DetailField
														label={t(
															"org-admin.settings.preferences.contacts.fields.title",
														)}
														value={
															primaryContactForm.primaryContact?.title ? (
																primaryContactForm.primaryContact?.title
															) : (
																<EmptyValue />
															)
														}
														isEditMode={isPrimaryEditing}
														editComponent={
															<primaryContactForm.AppField name="title">
																{(field) => (
																	<field.InputField
																		field={field}
																		data-testid="org-preferences-primary-contact-title-input"
																		label={t(
																			"org-admin.settings.preferences.contacts.fields.title",
																		)}
																		placeholder={t(
																			"org-admin.settings.preferences.contacts.placeholders.enterTitle",
																		)}
																		isDisabled={!isUserSelected}
																	/>
																)}
															</primaryContactForm.AppField>
														}
													/>

													<DetailField
														label={t(
															"org-admin.settings.preferences.contacts.fields.email",
														)}
														value={
															primaryContactForm.primaryContact?.email ? (
																<div className="flex items-center gap-2">
																	<Mail01 size={15} className="text-gray-400" />
																	{primaryContactForm.primaryContact.email}
																</div>
															) : null
														}
														isEditMode={isPrimaryEditing}
														editComponent={
															<primaryContactForm.AppField name="email">
																{(field) => (
																	<field.InputField
																		field={field}
																		label={t(
																			"org-admin.settings.preferences.contacts.fields.email",
																		)}
																		isDisabled
																	/>
																)}
															</primaryContactForm.AppField>
														}
													/>
												</DetailFieldSection>

												<DetailFieldSection>
													<DetailField
														label={t(
															"org-admin.settings.preferences.contacts.fields.phone",
														)}
														value={
															primaryContactForm.primaryContact?.phone ? (
																<div className="flex items-center gap-2">
																	<Phone size={15} className="text-gray-400" />
																	<PhoneNumber
																		phoneNumber={
																			primaryContactForm.primaryContact.phone
																		}
																	/>
																</div>
															) : null
														}
														isEditMode={isPrimaryEditing}
														editComponent={
															<primaryContactForm.AppField
																name="phone"
																validators={{
																	onBlur: ({ value }: { value: string }) => {
																		if (!value) return undefined;
																		if (value.replace(/\D/g, "").length !== 10)
																			return t(
																				"common.validation.phoneTenDigits",
																			);
																		return undefined;
																	},
																}}
																listeners={{
																	onChange: ({
																		value,
																		fieldApi,
																	}: {
																		value: string;
																		fieldApi: { setValue: (v: string) => void };
																	}) => {
																		const cleaned = value
																			.replace(/\D/g, "")
																			.slice(0, 10);
																		if (cleaned !== value)
																			fieldApi.setValue(cleaned);
																	},
																}}
															>
																{(field) => (
																	<field.InputField
																		field={field}
																		label={t(
																			"org-admin.settings.preferences.contacts.fields.phone",
																		)}
																		placeholder={t(
																			"org-admin.settings.preferences.contacts.placeholders.enterPhone",
																		)}
																		isDisabled={!isUserSelected}
																		maxLength={10}
																		inputMode="numeric"
																	/>
																)}
															</primaryContactForm.AppField>
														}
													/>

													<DetailField
														label={t(
															"org-admin.settings.preferences.contacts.fields.address",
														)}
														value={
															primaryContactForm.primaryContact?.address ? (
																<div className="flex items-center gap-2">
																	<MarkerPin01
																		size={15}
																		className="text-gray-400"
																	/>
																	{formatAddress(
																		primaryContactForm.primaryContact.address,
																	)}
																</div>
															) : null
														}
														isEditMode={isPrimaryEditing}
														editComponent={
															<div className="space-y-3">
																<primaryContactForm.AppField name="street">
																	{(field) => (
																		<field.InputField
																			field={field}
																			label={t(
																				"org-admin.settings.preferences.contacts.fields.street",
																			)}
																			placeholder={t(
																				"org-admin.settings.preferences.contacts.placeholders.enterStreet",
																			)}
																			isDisabled={!isUserSelected}
																		/>
																	)}
																</primaryContactForm.AppField>
																<div className="grid grid-cols-3 gap-2">
																	<primaryContactForm.AppField name="city">
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.city",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterCity",
																				)}
																				isDisabled={!isUserSelected}
																			/>
																		)}
																	</primaryContactForm.AppField>
																	<primaryContactForm.AppField
																		name="state"
																		validators={{
																			onBlur: ({
																				value,
																			}: {
																				value: string;
																			}) => {
																				if (!value) return undefined;
																				if (!isValidUSState(value))
																					return t(
																						"common.validation.usStateInvalid",
																					);
																				return undefined;
																			},
																		}}
																	>
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.state",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterState",
																				)}
																				isDisabled={!isUserSelected}
																			/>
																		)}
																	</primaryContactForm.AppField>
																	<primaryContactForm.AppField
																		name="zipcode"
																		validators={{
																			onBlur: ({
																				value,
																			}: {
																				value: string;
																			}) => {
																				if (!value) return undefined;
																				if (!isValidUSZipcode(value))
																					return t(
																						"common.validation.zipcodeInvalid",
																					);
																				return undefined;
																			},
																		}}
																		listeners={{
																			onChange: ({
																				value,
																				fieldApi,
																			}: {
																				value: string;
																				fieldApi: {
																					setValue: (v: string) => void;
																				};
																			}) => {
																				const cleaned = value
																					.replace(/\D/g, "")
																					.slice(0, 5);
																				if (cleaned !== value)
																					fieldApi.setValue(cleaned);
																			},
																		}}
																	>
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.zipcode",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterZipcode",
																				)}
																				isDisabled={!isUserSelected}
																				maxLength={5}
																				inputMode="numeric"
																			/>
																		)}
																	</primaryContactForm.AppField>
																</div>
															</div>
														}
													/>
												</DetailFieldSection>
											</DetailFieldGrid>
										);
									}}
								</primaryContactForm.Subscribe>
							</ContactCard>

							{/* Billing Contact Card */}
							<ContactCard
								title={t(
									"org-admin.settings.preferences.contacts.billingContact",
								)}
								icon={CreditCard01}
								isEditing={isBillingEditing && canEditBillingContact.allowed}
								onEdit={
									canEditBillingContact.allowed
										? handleBillingEdit
										: undefined
								}
								onCancel={handleBillingCancel}
								onSave={handleBillingSave}
								isSaving={billingContactForm.isUpdating}
								isSaveDisabled={!billingContactForm.isDirty}
								className="w-1/2"
							>
								<billingContactForm.Subscribe
									selector={(state) => state.values}
								>
									{(values) => {
										// Check if a user has been selected OR a custom name has been entered
										const hasUserOrName =
											!!values.userId ||
											(!!values.name && values.name.length > 0);
										// True when the billing contact resolves to an actual org user
										const isBillingUser = !!values.userId;
										// Disable all fields when "Same as Primary" is checked
										const fieldsDisabled =
											(values.sameAsPrimary ?? false) || !hasUserOrName;

										return (
											<>
												{isBillingEditing && (
													<div className="mb-4">
														<Checkbox
															isSelected={values.sameAsPrimary ?? false}
															label={t(
																"org-admin.settings.preferences.contacts.sameAsPrimary",
															)}
															onChange={(checked) =>
																billingContactForm.handleSameAsPrimaryChange(
																	checked,
																)
															}
															// className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
														/>
													</div>
												)}

												{
													<DetailFieldGrid>
														<DetailFieldSection>
															<DetailField
																label={t(
																	"org-admin.settings.preferences.contacts.fields.name",
																)}
																value={
																	[
																		billingContactForm.billingContact
																			?.firstName,
																		billingContactForm.billingContact?.lastName,
																	]
																		.filter(Boolean)
																		.join(" ") || null
																}
																isEditMode={isBillingEditing}
																editComponent={
																	<billingContactForm.AppField name="name">
																		{(field) => (
																			<field.ComboBoxField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.name",
																				)}
																				items={billingUserSelectItems}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.selectOrEnterName",
																				)}
																				allowsCustomValue
																				isDisabled={
																					values.sameAsPrimary ?? false
																				}
																				onLoadMore={
																					billingContactForm.fetchNextUsersPage
																				}
																				hasNextPage={
																					billingContactForm.hasNextUsersPage
																				}
																				isFetchingNextPage={
																					billingContactForm.isFetchingNextUsersPage
																				}
																				onItemSelect={(key) => {
																					if (key) {
																						billingContactForm.handleUserSelect(
																							key as string,
																						);
																					}
																				}}
																			>
																				{(item: SelectItemType) => (
																					<SelectItem
																						id={item.id}
																						textValue={item.label}
																					>
																						<div className="flex flex-col">
																							<span>{item.label}</span>
																							<span className="text-xs text-gray-500">
																								{item.supportingText}
																							</span>
																						</div>
																					</SelectItem>
																				)}
																			</field.ComboBoxField>
																		)}
																	</billingContactForm.AppField>
																}
															/>

															<DetailField
																label={t(
																	"org-admin.settings.preferences.contacts.fields.title",
																)}
																value={
																	billingContactForm.billingContact?.title ? (
																		billingContactForm.billingContact?.title
																	) : (
																		<EmptyValue />
																	)
																}
																isEditMode={isBillingEditing}
																editComponent={
																	<billingContactForm.AppField name="title">
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.title",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterTitle",
																				)}
																				isDisabled={fieldsDisabled}
																			/>
																		)}
																	</billingContactForm.AppField>
																}
															/>

															<DetailField
																label={t(
																	"org-admin.settings.preferences.contacts.fields.email",
																)}
																value={
																	billingContactForm.billingContact?.email ? (
																		<div className="flex items-center gap-2">
																			<Mail01
																				size={15}
																				className="text-gray-400"
																			/>
																			{billingContactForm.billingContact.email}
																		</div>
																	) : null
																}
																isEditMode={isBillingEditing}
																editComponent={
																	<billingContactForm.AppField
																		name="email"
																		validators={{
																			onBlur: ({
																				value,
																				fieldApi,
																			}: {
																				value: string;
																				fieldApi: {
																					form: {
																						state: {
																							values: {
																								userId?: string | null;
																							};
																						};
																					};
																				};
																			}) => {
																				// Only validate format for free-text (non-user) billing contacts
																				if (fieldApi.form.state.values.userId)
																					return undefined;
																				if (!value) return undefined;
																				if (
																					!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
																						value,
																					)
																				)
																					return t(
																						"common.validation.emailInvalid",
																					);
																				return undefined;
																			},
																		}}
																	>
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.email",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterEmail",
																				)}
																				isDisabled={
																					fieldsDisabled || isBillingUser
																				}
																			/>
																		)}
																	</billingContactForm.AppField>
																}
															/>
														</DetailFieldSection>

														<DetailFieldSection>
															<DetailField
																label={t(
																	"org-admin.settings.preferences.contacts.fields.phone",
																)}
																value={
																	billingContactForm.billingContact?.phone ? (
																		<div className="flex items-center gap-2">
																			<Phone
																				size={15}
																				className="text-gray-400"
																			/>
																			<PhoneNumber
																				phoneNumber={
																					billingContactForm.billingContact
																						.phone
																				}
																			/>
																		</div>
																	) : null
																}
																isEditMode={isBillingEditing}
																editComponent={
																	<billingContactForm.AppField
																		name="phone"
																		validators={{
																			onBlur: ({
																				value,
																			}: {
																				value: string;
																			}) => {
																				if (!value) return undefined;
																				if (
																					value.replace(/\D/g, "").length !== 10
																				)
																					return t(
																						"common.validation.phoneTenDigits",
																					);
																				return undefined;
																			},
																		}}
																		listeners={{
																			onChange: ({
																				value,
																				fieldApi,
																			}: {
																				value: string;
																				fieldApi: {
																					setValue: (v: string) => void;
																				};
																			}) => {
																				const cleaned = value
																					.replace(/\D/g, "")
																					.slice(0, 10);
																				if (cleaned !== value)
																					fieldApi.setValue(cleaned);
																			},
																		}}
																	>
																		{(field) => (
																			<field.InputField
																				field={field}
																				label={t(
																					"org-admin.settings.preferences.contacts.fields.phone",
																				)}
																				placeholder={t(
																					"org-admin.settings.preferences.contacts.placeholders.enterPhone",
																				)}
																				isDisabled={fieldsDisabled}
																				maxLength={10}
																				inputMode="numeric"
																			/>
																		)}
																	</billingContactForm.AppField>
																}
															/>

															<DetailField
																label={t(
																	"org-admin.settings.preferences.contacts.fields.address",
																)}
																value={
																	billingContactForm.billingContact?.address ? (
																		<div className="flex items-center gap-2">
																			<MarkerPin01
																				size={15}
																				className="text-gray-400"
																			/>
																			{formatAddress(
																				billingContactForm.billingContact
																					.address,
																			)}
																		</div>
																	) : null
																}
																isEditMode={isBillingEditing}
																editComponent={
																	<div className="space-y-3">
																		<billingContactForm.AppField name="street">
																			{(field) => (
																				<field.InputField
																					field={field}
																					label={t(
																						"org-admin.settings.preferences.contacts.fields.street",
																					)}
																					placeholder={t(
																						"org-admin.settings.preferences.contacts.placeholders.enterStreet",
																					)}
																					isDisabled={fieldsDisabled}
																				/>
																			)}
																		</billingContactForm.AppField>
																		<div className="grid grid-cols-3 gap-2">
																			<billingContactForm.AppField name="city">
																				{(field) => (
																					<field.InputField
																						field={field}
																						label={t(
																							"org-admin.settings.preferences.contacts.fields.city",
																						)}
																						placeholder={t(
																							"org-admin.settings.preferences.contacts.placeholders.enterCity",
																						)}
																						isDisabled={fieldsDisabled}
																					/>
																				)}
																			</billingContactForm.AppField>
																			<billingContactForm.AppField
																				name="state"
																				validators={{
																					onBlur: ({
																						value,
																					}: {
																						value: string;
																					}) => {
																						if (!value) return undefined;
																						if (!isValidUSState(value))
																							return t(
																								"common.validation.usStateInvalid",
																							);
																						return undefined;
																					},
																				}}
																			>
																				{(field) => (
																					<field.InputField
																						field={field}
																						label={t(
																							"org-admin.settings.preferences.contacts.fields.state",
																						)}
																						placeholder={t(
																							"org-admin.settings.preferences.contacts.placeholders.enterState",
																						)}
																						isDisabled={fieldsDisabled}
																					/>
																				)}
																			</billingContactForm.AppField>
																			<billingContactForm.AppField
																				name="zipcode"
																				validators={{
																					onBlur: ({
																						value,
																					}: {
																						value: string;
																					}) => {
																						if (!value) return undefined;
																						if (!isValidUSZipcode(value))
																							return t(
																								"common.validation.zipcodeInvalid",
																							);
																						return undefined;
																					},
																				}}
																				listeners={{
																					onChange: ({
																						value,
																						fieldApi,
																					}: {
																						value: string;
																						fieldApi: {
																							setValue: (v: string) => void;
																						};
																					}) => {
																						const cleaned = value
																							.replace(/\D/g, "")
																							.slice(0, 5);
																						if (cleaned !== value)
																							fieldApi.setValue(cleaned);
																					},
																				}}
																			>
																				{(field) => (
																					<field.InputField
																						field={field}
																						label={t(
																							"org-admin.settings.preferences.contacts.fields.zipcode",
																						)}
																						placeholder={t(
																							"org-admin.settings.preferences.contacts.placeholders.enterZipcode",
																						)}
																						isDisabled={fieldsDisabled}
																						maxLength={5}
																						inputMode="numeric"
																					/>
																				)}
																			</billingContactForm.AppField>
																		</div>
																	</div>
																}
															/>
														</DetailFieldSection>
													</DetailFieldGrid>
												}

												{values.sameAsPrimary && isBillingEditing && (
													<p className="text-sm text-gray-500 italic">
														{t(
															"org-admin.settings.preferences.contacts.sameAsPrimary",
														)}{" "}
														{t(
															"org-admin.settings.preferences.contacts.sameAsPrimaryDescription",
														)}
													</p>
												)}
											</>
										);
									}}
								</billingContactForm.Subscribe>
							</ContactCard>
						</div>

						{/* Terminology and Labels Contact Card */}
						<ContactCard
							title={t(
								"org-admin.settings.preferences.terminology.sectionTitle",
							)}
							icon={File06}
							isEditing={
								isTerminologyEditing && canEditTerminology.allowed
							}
							onEdit={
								canEditTerminology.allowed
									? handleTerminologyEdit
									: undefined
							}
							onCancel={handleTerminologyCancel}
							onSave={handleTerminologySave}
							isSaving={isTerminologySaving}
							saveTestId="org-preferences-terminology-save-btn"
							editTestId="org-preferences-terminology-edit-btn"
							className="w-full"
						>
							<DetailField
								label={t(
									"org-admin.settings.preferences.terminology.subOrganizationLabel",
								)}
								value={t("subOrganization.entityLabel")}
								isEditMode={isTerminologyEditing}
								className="flex flex-row gap-24"
								editComponent={
									<Input
										label={t(
											"org-admin.settings.preferences.terminology.subOrganizationLabel",
										)}
										placeholder={t(
											"org-admin.settings.preferences.terminology.placeholder",
										)}
										value={subOrganizationLabel}
										onChange={setSubOrganizationLabel}
										data-testid="org-preferences-terminology-label-input"
										className="flex flex-row gap-24"
										hint={t(
											"org-admin.settings.preferences.terminology.subOrganizationLabelHint",
										)}
									/>
								}
							/>
						</ContactCard>
					</section>
				)}
			</div>
		</div>
	);
}
